import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { storage } from '@uhg-abyss/web/tools/storage';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Skeleton as AbyssSkeleton } from '@uhg-abyss/web/ui/Skeleton';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import {
  Constants,
  DENTAL_STANDALONE,
  GYMBENEFITROLLUPCODE,
  PRACTITIONER_NAME_FOR_ANALYTICS,
  ProviderType,
  RecentSearchesSearchTypes,
  RxBenefitsTypes,
  SPECIALITYROLLUPCODEICARE,
  SectionType,
} from '../../../../common/Constants';
import { phoneOnly } from '../../../../common/ConstantsStyles';
import { handleRecentSearchesChipClick } from '../../../../common/RecentActivity/helper';
import { RecentSearchesResponse } from '../../../../common/RecentActivity/RecentSearchesCard/RecentSearchesCard';
import { convertObjectToUrlParams } from '../../../../common/Utils';
import { makeAllSlidesAriaHidden } from '../../../../common/Utils/a11yUtils/a11yUtils';
import { getIntValue } from '../../../../common/Utils/getIntValue';
import { getPlanSelectorVariables } from '../../../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { getTelemetryCoverageType } from '../../../../frontends/ProviderSearch/routes/MapView/utils';
import { useAutoCompleteTelemetry } from '../../../../hooks/useAutoCompleteTelemetry';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useHsidUuid } from '../../../../hooks/useHsidUuid/useHsidUuid';
import { useLagoon } from '../../../../hooks/useLagoon';
import {
  useDeleteRecentSearches,
  useGetRecentSearchesResults,
  usePlanYear,
} from '../../../../hooks/useRecentSearches/useRecentSearches';
import { getSearchInfo } from '../../../../hooks/useTypeAheadData/utils';
import { ResponseHeaders } from '../../../../models/ResponseHeaders';
import { useAnalyticsStore } from '../../../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../../../store/useAnalyticsStore/analyticsStore';
import { useCareTeamStore } from '../../../../store/useCareTeamStore';
import { useCeOffersStore } from '../../../../store/useCeOffersStore';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../../../store/useDetailsStore';
import { DetailsStore } from '../../../../store/useDetailsStore/detailsStore';
import { usePCPStore } from '../../../../store/usePCPStore';
import { PCPSearchStore } from '../../../../store/usePCPStore/pcpStore';
import { useSearchStore } from '../../../../store/useSearchStore';
import { SearchState } from '../../../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import { useVirtualCareStore } from '../../../../store/useVirtualCareStore';
import { getLagoonConfigValue } from '../../../../utils/providerSearch.utils';
import { handleSearchBarSuggestionClickOrEnter } from '../../../../utils/typeahead.utils';
import {
  getCurrentMember,
  getProviderType,
  useIsEligibleForStandaloneDental,
} from '../../../../utils/user.utils';
import { ConstantsLagoon } from '../../../ConstantsLagoon';
import { ConstantsRoutes } from '../../../ConstantsRoutes';
import {
  CustomAttributesBlock,
  convertTypeaheadProviderIdAndType,
  getTypeAheadCategory,
} from '../../../Utils/adobeTrackUtils/adobeTrackUtils';
import {
  getGeoLocationFromStorage,
  getLinkNameForAnalytics,
  getMpCodes,
  getProgramPromotionsForTypeahead,
  getUniqueVirtualCareProviders,
  getVirtualCareProvidersForTypeaheadFromEligibilities,
} from '../utils';
import { DisplayCoverageTypes } from './DisplayCoverageTypes';
import GuestDropdownBeforeMessage from './GuestDropdownBeforeMessage';
import { HighlightedSuggestion } from './HighlightedSuggestion';
import {
  Divider,
  DropdownContainer,
  SuggestionListContainer,
  SuggestionListItem,
  SuggestionSectionItem,
  isPrimaryActiveSuggestion,
  keywordSearchDropdownIconColor,
} from './KeywordSearchDropdown.styled';
import { Skeleton } from './Skeleton';
import { SuggestionList } from './SuggestionList';
import { UserTextSearch } from './UserTextSearch';

type Props = {
  activeSuggestion: number;
  headers: ResponseHeaders;
  isKeywordSearchLoading: boolean;
  typeAheadSuggestions?: any;
  keywordSearchTerm: string;
  isLoading: boolean;
  setIsFocusedOnKeywordSearchInput: (val: boolean) => void;
  enter: boolean;
  dentalCoverage: string;
  medicalCoverage: string;
  memberDDP: string;
  visionCoverage: string;
  autoCompleteCallCompleted: boolean;
  getCoverageType: Array<string>;
  hideDisabledTypeahead: boolean;
  blurKeywordSearchInput: () => void;
  checkForEmptyLocation: () => void;
  enableUESSuggestionMatch: boolean;
  handleGeneralSearchClick?: () => void;
  isLLMGeneralSearchEnabled?: boolean;
  specialityRollUpCodes?: string;
  search: string;
  isRxEligible?: boolean;
  onCompareProviderTypesClick?: () => void;
};

type TitleContentProps = {
  item: RecentSearchesResponse;
};

export function formatSpecialtiesString(
  specialties: string,
  t: (key: string) => string
): string {
  if (!specialties) return '';
  const arr = specialties
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const shown = arr.slice(0, 2).join(', ');
  const moreCount = arr.length - 2;
  return arr.length > 2 ? `${shown} (${moreCount} ${t('More')})` : shown;
}

const handleOnRecentSearchesSuggestionClickOrEnter = ({
  value,
  navigate,
  chipStore,
  setTypeaheadSearchStore,
  setAnalyticsState,
  setChipValue,
  setCoverageType,
  setDetailsStore,
  setSearchStore,
  specialityPreferenceTable,
  enableRetriveTypeAheadData,
  isEligibleForStandaloneDental,
}) => {
  const analyticsLinkName = `recent search typeahead:${
    value.searchType?.toLowerCase() ===
    RecentSearchesSearchTypes.PRACTITIONER_DETAIL.toLowerCase()
      ? PRACTITIONER_NAME_FOR_ANALYTICS
      : value.psxSearchTerm
  }`;
  handleRecentSearchesChipClick(
    value,
    navigate,
    analyticsLinkName,
    {
      chipStore,
      setTypeaheadSearchStore,
      setAnalyticsState,
      setChipValue,
      setCoverageType,
      setDetailsStore,
      setSearchStore,
    },
    specialityPreferenceTable,
    enableRetriveTypeAheadData,
    isEligibleForStandaloneDental
  );
  adobeLinkTrackEvent({
    name: analyticsLinkName,
    location: 'body:typeahead recent-searches',
  });
};

export const KeywordSearchDropdown = ({
  activeSuggestion,
  headers,
  isKeywordSearchLoading,
  typeAheadSuggestions,
  keywordSearchTerm,
  isLoading,
  setIsFocusedOnKeywordSearchInput,
  enter,
  visionCoverage,
  dentalCoverage,
  medicalCoverage,
  memberDDP,
  getCoverageType,
  autoCompleteCallCompleted,
  hideDisabledTypeahead,
  blurKeywordSearchInput,
  checkForEmptyLocation,
  handleGeneralSearchClick,
  isLLMGeneralSearchEnabled = false,
  isRxEligible = false,
  specialityRollUpCodes = '',
  search: searchValue,
  onCompareProviderTypesClick,
}: Props) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);
  const currentMember = getCurrentMember();
  const isIFPEarlyPay = !!currentMember?.isIFPEarlyPay;
  const isGuestUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.IS_GUEST) || false;
  const lagoonTableName = isIFPEarlyPay
    ? Constants.LAGOON_TABLE.IFP_EARLY_PAY_COMMON_SEARCHES
    : Constants.LAGOON_TABLE.COMMON_SEARCHES;
  const isEligibleForStandaloneDental = useIsEligibleForStandaloneDental();
  // filter out common dental standalone searches for non dental standalone members
  const commonSearchesData = useLagoon(lagoonTableName)().filter(
    (item) =>
      isEligibleForStandaloneDental || item.category !== DENTAL_STANDALONE
  );
  const { navigate } = useRouter();
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  const { onlineRetailers, setSearchStore } = useSearchStore(
    useShallow((state: SearchState) => ({
      setSearchStore: state.setSearchStore,
      onlineRetailers: state.searchStore.onlineRetailers,
    }))
  );
  const storedLocation = getGeoLocationFromStorage();
  const { name = '', latitude: geoLat, longitude: geoLong } = storedLocation;
  const [, autoCompleteTelemetry] = useAutoCompleteTelemetry({});
  const typeaheadParams = useLagoon(Constants.LAGOON_TABLE.TYPEAHEAD_PARAMS)();

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { setCoverageType, setChipValue } = chipStore;

  const { setAnalyticsState } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      setAnalyticsState: state.setAnalyticsState,
    }))
  );
  const { setPCPSearchState, choosePCP, dependentSeqNbr } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      setPCPSearchState: state.setPCPSearchState,
      choosePCP: state.pcpSearchState.choosePCP,
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
    }))
  );
  const { setDetailsStore } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );
  const { setShowMaximumProviderLimitBanner } = useCareTeamStore((state) => ({
    setShowMaximumProviderLimitBanner: state.setShowMaximumProviderLimitBanner,
  }));
  const [searchParams] = useSearchParams();
  const { search, isSuggestionClickOrEnter } = getSearchInfo(searchParams);
  const [correlationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );

  const [
    enableRetriveTypeAheadDataFlag,
    enableSearchResultsV2,
    isTypeaheadBehavioralHealthTagSuppressed,
    isEnabledSearchBarRecentSearches,
    dentalSearchLagoonFlag,
    visionSearchLagoonFlag,
    enableAutoCompleteTelemetry,
    enableNonEnglishCharacters,
    showGuestTypeaheadCoverageLink,
    enableAutocompleteTaggingChanges,
    enableAutoCompletePhoneNumberAndNpiErrorMessage,
    enableRxBenefitsSection,
    enableGymenableMapGymBenefits,
    enableProgramPromotion,
    enableVirtualCareEpic,
    enableVirtualCareProvidersInTypeahead,
    enableCompareProviderTypes,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RETRIVE_TYPEAHEAD_DATA,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RESULTS_V2,
    ConstantsLagoon.FEATURE_FLAGS.SUPPRESS_TYPEAHEAD_BH_TAG,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_BAR_RECENT_SEARCHES,
    'TYPEAHEAD_DENTAL_SEARCH_STARTS_HERE_ENABLED',
    'TYPEAHEAD_VISION_SEARCH_STARTS_HERE_ENABLED',
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RELEVANCY_EVENTS_DASHBOARD,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NON_ENGLISH_CHARACTERS_TYPEAHEAD,
    ConstantsLagoon.FEATURE_FLAGS.GUEST_TYPEAHEAD_COVERAGE_LINK,
    ConstantsLagoon.FEATURE_FLAGS.AUTOCOMPLETE_TAGGING_CHANGES,
    ConstantsLagoon.FEATURE_FLAGS.AUTOCOMPLETE_ERROR_MESSAGE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RX_BENEFITS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAPGYM_BENEFITS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PROGRAM_PROMOTION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_PROVIDERS_IN_TYPEAHEAD,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COMPARE_PROVIDER_TYPES,
  ]);

  const { planToken } = getPlanSelectorVariables();

  const allConfigs = useLagoon('config');
  const programPromotionsRollupCodesTable = useLagoon(
    'program-promotions-pseudo-rollupcode-mapping'
  )();
  const uesVirtualCareKeywordMappingTable = useLagoon(
    'ues-virtual-care-keyword-mapping'
  )();

  const enablePseudoRollupCodeOnlyMatch = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PSEUDO_ROLLUP_CODE_ONLY_MATCH'
  );

  const enableVirtualCareNameSearchPsxLogic = getLagoonConfigValue(
    allConfigs,
    'ENABLE_VIRTUAL_CARE_NAME_SEARCH_PSX_LOGIC'
  );

  const virtualCareNameSearchAlternatives = getLagoonConfigValue(
    allConfigs,
    'VIRTUAL_CARE_NAME_SEARCH_ALTERNATIVES'
  );

  const enableProgramPromotionInTypeahead = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PROGRAM_PROMOTION_IN_TYPEAHEAD'
  );
  const ceOffersData = useCeOffersStore((state) => state.ceOffersData);
  const mpCodes = getMpCodes(
    specialityRollUpCodes,
    programPromotionsRollupCodesTable
  );
  const programPromotions = getProgramPromotionsForTypeahead(
    ceOffersData,
    mpCodes
  );

  const isMemberGymBenefitEligible =
    enableGymenableMapGymBenefits &&
    currentMember?.fitnessCoverageEligibility === 'Y' &&
    specialityRollUpCodes?.includes(GYMBENEFITROLLUPCODE);

  const isProgramPromotionsEligible =
    enableProgramPromotion &&
    enableProgramPromotionInTypeahead === 'Y' &&
    programPromotions?.length > 0;

  const eligibilities = useVirtualCareStore((state) => state.eligibilities);
  const virtualCareProviders =
    getVirtualCareProvidersForTypeaheadFromEligibilities({
      suggestion: typeAheadSuggestions,
      uesVirtualCareKeywordMappingTable,
      eligibilities: eligibilities[planToken],
      enablePseudoRollupCodeOnlyMatch,
      enableVirtualCareNameSearchPsxLogic,
      keywordSearchTerm,
      virtualCareNameSearchAlternatives,
    });
  const uniqueVirtualCareProviders =
    getUniqueVirtualCareProviders(virtualCareProviders);

  // virtual care provider suggestions should be hidden for standalone dental members since they're under medical coverage
  const shouldShowVirtualCareProviders =
    enableVirtualCareEpic &&
    enableVirtualCareProvidersInTypeahead &&
    uniqueVirtualCareProviders?.length > 0 &&
    !isEligibleForStandaloneDental;

  const enableRetriveTypeAheadData =
    enableRetriveTypeAheadDataFlag && enableSearchResultsV2;

  const displayDentalSearchStartsHere =
    dentalCoverage && dentalSearchLagoonFlag;
  const displayVisionSearchStartsHere =
    visionCoverage && visionSearchLagoonFlag;
  const isRxBenefitEligible =
    enableRxBenefitsSection &&
    currentMember?.rxEligibility === RxBenefitsTypes.CARVE_IN &&
    isRxEligible;

  const hasBehavioralHealthCoverage =
    getCoverageType?.includes('B') || getCoverageType?.includes('OBH');

  const isCompareProviderTypesKeywordMatch = (() => {
    const normalizedSearchTerm = keywordSearchTerm?.trim().toLocaleLowerCase();

    if (!normalizedSearchTerm) {
      return false;
    }

    const translatedKeywords = [
      t('BH_COMPARE_PROVIDER_TYPES.PSYCHOLOGIST'),
      t('BH_COMPARE_PROVIDER_TYPES.PSYCHIATRIST'),
      t('BH_COMPARE_PROVIDER_TYPES.PSYCHIATRIC'),
      t('BH_COMPARE_PROVIDER_TYPES.ADVANCED_PSYCHIATRIC_NP'),
    ]
      .map((keyword) => keyword?.trim().toLocaleLowerCase())
      .filter(Boolean);

    const minMatchLength = 3;
    const words = normalizedSearchTerm.split(/\s+/).filter(Boolean);

    const isMatch = words.some(
      (word) =>
        word.length >= minMatchLength &&
        translatedKeywords.some((keyword) => keyword.includes(word))
    );

    return isMatch;
  })();

  const isCompareProviderTypesEligible =
    enableCompareProviderTypes &&
    hasBehavioralHealthCoverage &&
    isCompareProviderTypesKeywordMatch &&
    (typeAheadSuggestions?.length ?? 0) > 0;

  const [planYear] = usePlanYear();
  const hsidUuid = useHsidUuid();
  const [deleteRecentSearchRes, getDeleteRecentSearch] =
    useDeleteRecentSearches({});
  const { data: recentSearchesData, isLoading: isRecentSearchesLoading } =
    useGetRecentSearchesResults();
  const [recentSearchesResponse, setRecentSearchesResponse] = useState<
    RecentSearchesResponse[]
  >([]);

  useEffect(() => {
    if (recentSearchesData?.recentSearches) {
      setRecentSearchesResponse(recentSearchesData.recentSearches);
    }
  }, [recentSearchesData?.recentSearches]);
  useEffect(() => {
    if (deleteRecentSearchRes?.data?.deleteRecentSearch?.searchDetails) {
      setRecentSearchesResponse(
        deleteRecentSearchRes?.data?.deleteRecentSearch?.searchDetails
      );
    }
  }, [deleteRecentSearchRes]);

  const sortItems = (a, b) => {
    if (
      !a?.coverageType?.some((coverageCode: string) =>
        getCoverageType.includes(coverageCode)
      )
    ) {
      return 1;
    }
    if (
      !b?.coverageType?.some((coverageCode: string) =>
        getCoverageType.includes(coverageCode)
      )
    ) {
      return -1;
    }

    return 0;
  };

  useEffect(() => {
    setSearchStore({ onlineRetailers });

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.IS_PROVIDER_CARD_SELECTED,
      false
    );
  }, []);

  const specialityPreferenceTable = useLagoon(
    Constants.LAGOON_TABLE.SPECIALITY_PREFERENCE
  )()?.map((item) => ({
    rollupCode: item.rollupCode,
    specialityCode: item.specialityCode,
  }));

  if (isKeywordSearchLoading) {
    const text = t('SEARCH_TIME_NOTIFICATION');
    return <Skeleton text={text} variant="alternate" />;
  }

  const searchInputOptionLocation = 'search-input-option-list';

  function adobeLinkTrack(value, index, typeAheadProviderLinks = false) {
    const customAttributesBlock =
      value?.searchType === 'Speciality' || typeAheadProviderLinks
        ? undefined
        : ({
            correlationId: headers?.correlationId,
            ...convertTypeaheadProviderIdAndType(value),
          } as CustomAttributesBlock);

    adobeLinkTrackEvent({
      name: getLinkNameForAnalytics(value),
      location: `body:${searchInputOptionLocation}:${getTypeAheadCategory(
        value?.providerType
      )}`,
      type: 'internal',
      searchBlock: {
        linkPosition: index + 1 || '1',
      },
      customAttributesBlock,
    });
  }

  const onChipClicked = (value, coverageType) => {
    adobeLinkTrackEvent({
      name: value,
      location: `body:${searchInputOptionLocation}`,
    });
    const chipStoreValues = {
      chipValue: value,
      coverageType,
    };
    setChipValue(value);
    setCoverageType(coverageType);
    const urlParams = convertObjectToUrlParams(chipStore, {
      ...chipStoreValues,
    });
    navigate(`/${urlParams}`, { replace: true });
  };

  const radius = getIntValue(
    typeaheadParams,
    Constants.TYPEAHEAD_PARAMS.RADIUS,
    Constants.TYPEAHEAD_PARAMS.RADIUS_DEFAULT
  );

  const getSectionType = (val) => {
    switch (val.toLowerCase()) {
      case ProviderType.PRACTITIONER.toLocaleLowerCase():
        return SectionType.PROVIDER;
      case SectionType.PROVIDER_GROUP.toLowerCase():
        return SectionType.PROVIDER_GROUP;
      default:
        return val;
    }
  };

  const onMoreResultClick = (item: any, searchType: string) => {
    const urlParams = convertObjectToUrlParams({} as ChipState, {
      search: searchValue,
      searchType,
      sectionType: getSectionType(item.providerType),
    });

    navigate(
      `${ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.path}${urlParams}`
    );
  };

  const commonTelemetryParamaters = {
    correlationId: headers?.correlationId ?? correlationIDSessionStorage,
    timestamp: new Date().toISOString(),
    searchLocation: {
      locationInputField: name || '',
      latitude: parseFloat(geoLat),
      longitude: parseFloat(geoLong),
      appliedSearchRadiusMiles: radius || 0,
    },
    searchPlatform: 'web',
  };

  const hitAutoCompleteTelemetry = async (updatedParams) => {
    await autoCompleteTelemetry({
      variables: updatedParams,
    });
  };

  const onSuggestionClick = (value, index) => {
    if (value) {
      typeAheadSuggestions?.filter((item) =>
        item?.hasOwnProperty('section')
      )?.[0]?.items &&
        storage.session.set(
          Constants.STORAGE_KEYS.SESSION.PROVIDER_LIST,
          typeAheadSuggestions?.filter((item) =>
            item?.hasOwnProperty('section')
          )?.[0]?.items
        );
      const telemetryNameSuggestions =
        typeAheadSuggestions?.length > 0 &&
        typeAheadSuggestions?.filter((item) =>
          item?.hasOwnProperty('section')
        )?.[0]?.items?.length > 0
          ? [
              ...typeAheadSuggestions
                ?.filter((item) => item?.hasOwnProperty('section'))[0]
                ?.items?.map((item) => ({
                  resultId: item?.providerId,
                  resultType: getProviderType(item?.providerType),
                  name: item?.displayName,
                  specialty: item?.speciality
                    ?.map((s) => s?.category)
                    .join(', '),
                  address: item?.displayAddress,
                  coverageType: item?.coverageType?.map(
                    getTelemetryCoverageType
                  ),
                })),
            ]
          : [];
      const telemetryNonNameSuggestions =
        typeAheadSuggestions?.length > 0
          ? [
              ...typeAheadSuggestions
                ?.filter((item) => item?.hasOwnProperty('suggestion'))
                ?.map((item) => ({
                  label: item?.suggestion,
                  coverage: item?.coverageType?.map(getTelemetryCoverageType),
                })),
            ]
          : [
              ...commonSearchesData?.map((item) => ({
                label: item?.psxKeyword,
                coverage: getTelemetryCoverageType(item?.coverageType),
              })),
            ];
      const updatedParams = {
        ...commonTelemetryParamaters,
        nameSuggestions: telemetryNameSuggestions ?? [],
        nonNameSuggestions: telemetryNonNameSuggestions ?? [],
        resultedInNullSearch:
          keywordSearchTerm?.length > 0 && typeAheadSuggestions?.length === 0,
        interpretedAsNameSearch:
          typeAheadSuggestions?.filter((item) =>
            item?.hasOwnProperty('section')
          )?.[0]?.items?.length > 0 && typeAheadSuggestions?.length === 1,
        searchQuery:
          keywordSearchTerm?.length > 0 ? keywordSearchTerm : value?.psxKeyword,
        selectedSuggestionLabel:
          value?.suggestion ?? value?.displayName ?? value?.psxKeyword ?? '',
        selectedSuggestionCoverageType: Array.isArray(value?.coverageType)
          ? getTelemetryCoverageType(value?.coverageType?.join(''))
          : getTelemetryCoverageType(value?.coverageType),
        selectedSuggestionType:
          typeAheadSuggestions?.length > 0 &&
          typeAheadSuggestions
            ?.filter((item) => item?.hasOwnProperty('suggestion'))
            ?.some((item) => item?.displayName === value?.suggestion)
            ? 'name'
            : 'non_name',
        selectedSuggestionOverallRank:
          typeAheadSuggestions?.length > 0 &&
          typeAheadSuggestions
            ?.filter((item) => item?.hasOwnProperty('suggestion'))
            ?.some((item) => item?.displayName === value?.suggestion)
            ? telemetryNonNameSuggestions?.length + index + 1
            : index + 1,
        selectedSuggestionInCategoryRank: index + 1,
        event: 'suggestionSelection',
      };
      if (enableAutoCompleteTelemetry) hitAutoCompleteTelemetry(updatedParams);
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_INDEX,
        index
      );
      setShowMaximumProviderLimitBanner(false);
      setIsFocusedOnKeywordSearchInput(false);
      const selectedValue =
        value?.psxKeyword && value?.translatedPsxKeyword
          ? { ...value, psxKeyword: value?.translatedPsxKeyword }
          : value;
      const searchTerm =
        selectedValue?.psxKeyword ||
        selectedValue?.suggestion ||
        selectedValue?.search;
      const isUserOnSameResultsPage =
        isSuggestionClickOrEnter &&
        search &&
        searchTerm &&
        searchTerm === search;
      // when user is on search results (suggestion search) page and clicks on the same suggestion, we do not want to trigger the search again.
      if (isUserOnSameResultsPage) {
        return;
      }
      adobeLinkTrack(value, index);
      handleSearchBarSuggestionClickOrEnter({
        enableRetriveTypeAheadData,
        specialityPreferenceTable,
        value: selectedValue,
        searchTerm: keywordSearchTerm,
        currentMember,
        getCoverageType,
        dependentSeqNbr,
        choosePCP,
        chipStore,
        setTypeaheadSearchStore,
        setAnalyticsState,
        setPCPSearchState,
        setChipValue,
        setCoverageType,
        setDetailsStore,
        setSearchStore,
        navigate,
        isEligibleForStandaloneDental,
      });
      checkForEmptyLocation();
    }
  };

  const shouldShowCommonSearches =
    isGuestUser || isEligibleForStandaloneDental
      ? getCoverageType.length > 0
      : medicalCoverage;

  const specialCharCheck = enableNonEnglishCharacters
    ? false
    : !/[A-Za-z0-9]/.test(keywordSearchTerm);

  const setMessage = () => {
    let message: string;
    if (specialCharCheck && keywordSearchTerm !== '') {
      message = t('Special char search');
    } else if (keywordSearchTerm?.length <= 1) {
      message = t('Short search');
    } else {
      message = enableAutoCompletePhoneNumberAndNpiErrorMessage
        ? t('no-results-found-for-phone-number-and-npi')
        : t('No Results Found');
    }
    return message;
  };

  const onSuggestionClickWithStartIndex = (commonSearchesStartIndex) => {
    onSuggestionClick(
      commonSearchesData[activeSuggestion - commonSearchesStartIndex],
      activeSuggestion - commonSearchesStartIndex
    );
  };

  const ChipText = {
    marginLeft: '$xs',
    fontWeight: '600',
    fontSize: '$lg',
    lineHeight: '20px',
    whiteSpace: 'normal',
    textAlign: 'left',
    overflow: 'hidden',
    display: 'block',
    width: '100%',
    minWidth: 0,
    wordBreak: 'break-word',
  };

  const TitleContent = ({ item }: TitleContentProps) => (
    makeAllSlidesAriaHidden(),
    (
      <Link
        data-auto-testid={`recent-search-typeahead-title`}
        data-testid={`recent-search-typeahead-title`}
        fontWeight="bold"
        hideIcon
        isStandardAnchor
        variant="alt-underlined"
      >
        <Text
          color="$tcolor"
          css={ChipText}
          data-testid={'recently-search-typeahead-term'}
        >
          {item.psxSearchTerm}
        </Text>
      </Link>
    )
  );

  const OnChipClickedUsingCategory = (chipCategory) => {
    if (activeSuggestion === 0) {
      if (Constants.CHIPS_CATEGORIES.DENTAL === chipCategory)
        onChipClicked(Constants.CHIPS_CATEGORIES.DENTAL, 'D');
      else if (Constants.CHIPS_CATEGORIES.VISION === chipCategory)
        onChipClicked(Constants.CHIPS_CATEGORIES.VISION, 'V');
    } else if (activeSuggestion > 0) {
      onSuggestionClickWithStartIndex(1);
    }
  };

  if (
    enter &&
    keywordSearchTerm?.trim().length < Constants.MIN_KEYWORD_SEARCHTERM_LENGTH
  ) {
    blurKeywordSearchInput();
    setIsFocusedOnKeywordSearchInput(false);
    if (displayDentalSearchStartsHere && displayVisionSearchStartsHere) {
      if (activeSuggestion === 0) {
        onChipClicked(Constants.CHIPS_CATEGORIES.DENTAL, 'D');
      } else if (activeSuggestion === 1) {
        onChipClicked(Constants.CHIPS_CATEGORIES.VISION, 'V');
      } else if (activeSuggestion > 1) {
        // When both dental and vision search starts here are displayed, common searches index starts from 2.
        onSuggestionClickWithStartIndex(2);
      }
    } else if (displayDentalSearchStartsHere) {
      // When both only dental search starts here is displayed, common searches index starts from 1.
      OnChipClickedUsingCategory(Constants.CHIPS_CATEGORIES.DENTAL);
    } else if (displayVisionSearchStartsHere) {
      // When both only vision search starts here is displayed, common searches index starts from 1.
      OnChipClickedUsingCategory(Constants.CHIPS_CATEGORIES.VISION);
    } else if (
      isEnabledSearchBarRecentSearches &&
      recentSearchesResponse?.length
    ) {
      const value = recentSearchesResponse?.[activeSuggestion];
      if (value) {
        handleOnRecentSearchesSuggestionClickOrEnter({
          value,
          navigate,
          chipStore,
          setTypeaheadSearchStore,
          setAnalyticsState,
          setChipValue,
          setCoverageType,
          setDetailsStore,
          setSearchStore,
          specialityPreferenceTable,
          enableRetriveTypeAheadData,
          isEligibleForStandaloneDental,
        });
      }
    } else {
      // When both dental and vision are not displayed, common searches index starts from 0.
      onSuggestionClickWithStartIndex(0);
    }
  }

  const numberOfOtherCoverageOptionsForNoKeywordInput =
    (displayDentalSearchStartsHere ? 1 : 0) +
    (displayVisionSearchStartsHere ? 1 : 0);

  const removeSearchTerm = (event, value, toClearAll = false) => {
    event.stopPropagation();
    const isSuperUser =
      storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;

    if (!isSuperUser && !isGuestUser) {
      getDeleteRecentSearch({
        variables: {
          clearAll: toClearAll,
          psxSearchTerm: value?.psxSearchTerm,
          pesSearchTerm: value?.pesSearchTerm,
          createdDate: value?.createdDate,
          planYear,
          ...(hsidUuid && { userId: hsidUuid }),
        },
      });
    }
  };

  const LoadingSkeleton = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: '26px',
        width: '100%',
      }}
    >
      {Array.from({ length: 5 }).map((_, idx) => (
        <div
          key={idx}
          style={{
            marginBottom: 15,
            marginTop: idx === 0 ? 10 : 0,
          }}
        >
          <AbyssSkeleton
            height={12}
            variant="alternate"
            width={mobileScreen ? '90%' : '40%'}
          />
        </div>
      ))}
    </div>
  );

  if (
    (keywordSearchTerm?.trim().length === 0 ||
      keywordSearchTerm?.trim().length === 1) &&
    !enter &&
    isEnabledSearchBarRecentSearches &&
    isRecentSearchesLoading
  ) {
    return <LoadingSkeleton />;
  }

  return (
    <React.Fragment>
      {(keywordSearchTerm?.trim().length === 0 ||
        keywordSearchTerm?.trim().length === 1) &&
      !enter ? (
        <React.Fragment>
          <Divider />
          {displayDentalSearchStartsHere || displayVisionSearchStartsHere ? (
            <Divider />
          ) : null}
          <SuggestionListContainer
            data-auto-testid="suggestion-list-container"
            data-testid="suggestion-list-container"
          >
            <DropdownContainer
              aria-label={
                isEnabledSearchBarRecentSearches &&
                recentSearchesResponse?.length > 0
                  ? 'typeahead recent searches'
                  : 'common searches'
              }
              data-testid="primary-dropdown-container"
              id="primary-search-bar-suggestions"
              role="listbox"
            >
              <GuestDropdownBeforeMessage
                show={showGuestTypeaheadCoverageLink}
              />

              {displayVisionSearchStartsHere ? (
                <SuggestionSectionItem
                  aria-posinset={displayDentalSearchStartsHere ? 2 : 1}
                  className={`${
                    activeSuggestion === (displayDentalSearchStartsHere ? 1 : 0)
                      ? 'active'
                      : ''
                  }`}
                  id={
                    activeSuggestion === (displayDentalSearchStartsHere ? 1 : 0)
                      ? 'primary-search-bar-result-suggestion'
                      : ''
                  }
                  role="option"
                >
                  <SuggestionListItem
                    className="suggestion-link-span"
                    css={{
                      '&.abyss-flex-root':
                        activeSuggestion ===
                        (displayDentalSearchStartsHere ? 1 : 0)
                          ? isPrimaryActiveSuggestion
                          : {},
                    }}
                    data-testid="search-input-box-vision-common-suggestion"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      ev.preventDefault();
                      setIsFocusedOnKeywordSearchInput(false);
                      onChipClicked(Constants.CHIPS_CATEGORIES.VISION, 'V');
                    }}
                    style={{ whiteSpace: 'pre' }}
                    tabIndex={-1}
                  >
                    {t('VISION_SEARCH')}
                    <IconSymbol icon="chevron_right" />
                  </SuggestionListItem>
                </SuggestionSectionItem>
              ) : null}
              {displayDentalSearchStartsHere ? (
                <SuggestionSectionItem
                  aria-posinset={1}
                  className={`${activeSuggestion === 0 ? 'active' : ''}`}
                  id={
                    activeSuggestion === 0
                      ? 'primary-search-bar-result-suggestion'
                      : ''
                  }
                  role="option"
                >
                  <SuggestionListItem
                    className="suggestion-link-span"
                    css={{
                      '&.abyss-flex-root':
                        activeSuggestion === 0 ? isPrimaryActiveSuggestion : {},
                    }}
                    data-testid="search-input-box-dental-common-suggestion"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      ev.preventDefault();
                      setIsFocusedOnKeywordSearchInput(false);
                      onChipClicked(Constants.CHIPS_CATEGORIES.DENTAL, 'D');
                    }}
                    style={{ whiteSpace: 'pre' }}
                    tabIndex={-1}
                  >
                    {t('DENTAL_SEARCH')}
                    <IconSymbol icon="chevron_right" />
                  </SuggestionListItem>
                </SuggestionSectionItem>
              ) : null}
              {isEnabledSearchBarRecentSearches &&
              recentSearchesResponse?.length > 0 &&
              !isRecentSearchesLoading
                ? recentSearchesResponse?.slice(0, 5).map((value, index) => (
                    <React.Fragment
                      key={
                        value?.psxSearchTerm || value?.pesSearchTerm || index
                      }
                    >
                      <SuggestionSectionItem
                        aria-posinset={
                          index + numberOfOtherCoverageOptionsForNoKeywordInput
                        }
                        className={
                          `${
                            activeSuggestion ===
                            index +
                              numberOfOtherCoverageOptionsForNoKeywordInput
                              ? 'active'
                              : ''
                          }` + (index === 0 ? ' first-child' : '')
                        }
                        key={value?.psxSearchTerm || value?.pesSearchTerm}
                        role="option"
                        style={{ width: '100%' }}
                      >
                        {index === 0 && (
                          <Text
                            data-testid="heading-recent-searches"
                            fontSize="md"
                            fontWeight="bold"
                          >
                            {t('RECENT_SEARCHES')}
                          </Text>
                        )}
                        {value?.psxSearchTerm && (
                          <div
                            data-testid="recent-searches-typeahead-container-list"
                            style={{
                              display: 'flex',
                              gap: '4px',
                              margin: 0,
                              flexShrink: 0,
                              justifyContent: 'space-between',
                              width: mobileScreen ? '100%' : 'auto',
                            }}
                          >
                            <SuggestionListItem
                              className="suggestion-link-span"
                              css={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexWrap: 'nowrap',
                                '&.abyss-flex-root':
                                  activeSuggestion ===
                                  index +
                                    numberOfOtherCoverageOptionsForNoKeywordInput
                                    ? isPrimaryActiveSuggestion
                                    : {},
                              }}
                              data-testid="recent-searches-typeahead-container"
                              key={value?.psxSearchTerm || value?.pesSearchTerm}
                              onClick={(e) => {
                                e.preventDefault();
                                handleOnRecentSearchesSuggestionClickOrEnter({
                                  value,
                                  navigate,
                                  chipStore,
                                  setTypeaheadSearchStore,
                                  setAnalyticsState,
                                  setChipValue,
                                  setCoverageType,
                                  setDetailsStore,
                                  setSearchStore,
                                  specialityPreferenceTable,
                                  enableRetriveTypeAheadData,
                                  isEligibleForStandaloneDental,
                                });
                                setIsFocusedOnKeywordSearchInput(false);
                              }}
                              style={{ whiteSpace: 'pre' }}
                              tabIndex={-1}
                            >
                              <span
                                className="icon-span"
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '4px',
                                  margin: 0,
                                  flexShrink: 0,
                                }}
                              >
                                <IconSymbol color="#196ECF" icon="history" />
                              </span>
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  minWidth: 0,
                                  flex: 1,
                                  gap: '4px',
                                  whiteSpace: 'normal',
                                }}
                              >
                                <TitleContent
                                  item={value}
                                  key={value.createdDate}
                                />

                                {value?.specialtyCategories && (
                                  <Text
                                    fontWeight="regular"
                                    style={{
                                      marginLeft: '4px',
                                      marginRight: '3px',
                                      fontSize: '16px',
                                      lineHeight: mobileScreen ? '1.7' : '20px',
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                      minWidth: 0,
                                    }}
                                  >
                                    {value?.specialtyCategories ===
                                    'General Search'
                                      ? ` - ${value?.specialtyCategories}`
                                      : formatSpecialtiesString(
                                          value.specialtyCategories,
                                          t
                                        )}
                                  </Text>
                                )}
                                <Text
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    paddingTop: '2px',
                                    minWidth: 0,
                                  }}
                                >
                                  <DisplayCoverageTypes
                                    enableAutocompleteTaggingChanges={
                                      enableAutocompleteTaggingChanges
                                    }
                                    getCoverageType={getCoverageType}
                                    hideDisabledTypeahead={false}
                                    isTypeaheadBehavioralHealthTagSuppressed={
                                      isTypeaheadBehavioralHealthTagSuppressed
                                    }
                                    item={value}
                                    memberDDP={memberDDP}
                                    useCategoryForCoverageType={true}
                                  />
                                </Text>
                              </span>
                            </SuggestionListItem>
                            <div
                              aria-label={`${t('SHARE_DRAWER_TEXT.REMOVE')} ${
                                value?.psxSearchTerm
                              }`}
                              data-auto-testid={`remove-recent-search-term`}
                              data-testid={`remove-recent-search-term`}
                              onClick={(e) => removeSearchTerm(e, value, false)}
                              style={{
                                cursor: 'pointer',
                              }}
                            >
                              <Tooltip
                                content={`${t('SHARE_DRAWER_TEXT.REMOVE')} ${
                                  value?.psxSearchTerm
                                }`}
                              >
                                <span>
                                  <IconSymbol
                                    color="$core.color.neutral.80"
                                    icon="close"
                                  />
                                </span>
                              </Tooltip>
                            </div>
                          </div>
                        )}
                      </SuggestionSectionItem>
                      {(index === 4 ||
                        index === recentSearchesResponse.length - 1) && (
                        <SuggestionListItem
                          aria-label={`${t('CLEAR_RECENT_SEARCHES')}`}
                          className="suggestion-link-span"
                          css={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            flexWrap: 'nowrap',
                            '&.abyss-flex-root':
                              activeSuggestion ===
                              index +
                                numberOfOtherCoverageOptionsForNoKeywordInput
                                ? isPrimaryActiveSuggestion
                                : {},
                          }}
                          data-auto-testid={`clear-recent-searches`}
                          data-testid={`clear-recent-searches`}
                          onClick={(e) => removeSearchTerm(e, null, true)}
                          style={{
                            cursor: 'pointer',
                          }}
                        >
                          <Text
                            color="$core.color.brand.60"
                            data-testid="clear-recent-searches"
                            fontSize="sm"
                            fontWeight="medium"
                          >
                            {t('CLEAR_RECENT_SEARCHES')}
                          </Text>
                        </SuggestionListItem>
                      )}
                    </React.Fragment>
                  ))
                : shouldShowCommonSearches
                ? commonSearchesData
                    .filter(({ coverageType }) =>
                      getCoverageType.includes(coverageType)
                    )
                    .map((value, index) => (
                      <SuggestionSectionItem
                        aria-posinset={
                          index +
                          numberOfOtherCoverageOptionsForNoKeywordInput +
                          1
                        }
                        className={
                          `${
                            activeSuggestion ===
                            index +
                              numberOfOtherCoverageOptionsForNoKeywordInput
                              ? 'active'
                              : ''
                          }` + (index === 0 ? ' first-child' : '')
                        }
                        id={
                          activeSuggestion ===
                          index + numberOfOtherCoverageOptionsForNoKeywordInput
                            ? 'primary-search-bar-result-suggestion'
                            : ''
                        }
                        key={value.id}
                        role="option"
                      >
                        {index === 0 && (
                          <Text
                            data-testid="heading-common-search"
                            fontSize="sm"
                            fontWeight="bold"
                          >
                            {t('COMMON_SEARCH')}
                          </Text>
                        )}
                        <SuggestionListItem
                          className="suggestion-link-span"
                          css={{
                            '&.abyss-flex-root':
                              activeSuggestion ===
                              index +
                                numberOfOtherCoverageOptionsForNoKeywordInput
                                ? isPrimaryActiveSuggestion
                                : {},
                          }}
                          data-testid="search-input-box-common-suggestion-link"
                          key={value.id}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            ev.preventDefault();
                            setSearchStore({ onlineRetailers: false });
                            onSuggestionClick(value, index);
                          }}
                          style={{ whiteSpace: 'pre' }}
                          tabIndex={-1}
                        >
                          <span className="icon-span">
                            <IconSymbol
                              color={keywordSearchDropdownIconColor}
                              icon="search"
                            />
                          </span>
                          <HighlightedSuggestion
                            keyword={value?.translatedPsxKeyword}
                            text={value?.translatedPsxKeyword}
                          />
                        </SuggestionListItem>
                      </SuggestionSectionItem>
                    ))
                : null}
            </DropdownContainer>
          </SuggestionListContainer>
        </React.Fragment>
      ) : (typeAheadSuggestions?.length ||
          specialityRollUpCodes === SPECIALITYROLLUPCODEICARE ||
          isMemberGymBenefitEligible ||
          isProgramPromotionsEligible ||
          isRxBenefitEligible ||
          isCompareProviderTypesEligible) &&
        !enter ? (
        <SuggestionList
          activeSuggestion={activeSuggestion}
          getCoverageType={getCoverageType}
          handleGeneralSearchClick={handleGeneralSearchClick}
          hideDisabledTypeahead={hideDisabledTypeahead}
          isCompareProviderTypesEligible={isCompareProviderTypesEligible}
          isLLMGeneralSearchEnabled={isLLMGeneralSearchEnabled}
          isMemberGymBenefitEligible={isMemberGymBenefitEligible}
          isProgramPromotionsEligible={isProgramPromotionsEligible}
          isRxBenefitEligible={isRxBenefitEligible}
          keywordSearchTerm={keywordSearchTerm}
          memberDDP={memberDDP}
          onCompareProviderTypesClick={onCompareProviderTypesClick}
          onMoreResultClick={onMoreResultClick}
          onSuggestionClick={onSuggestionClick}
          programPromotions={programPromotions}
          shouldShowVirtualCareProviders={shouldShowVirtualCareProviders}
          sortItems={sortItems}
          specialityRollUpCodes={specialityRollUpCodes}
          typeAheadSuggestions={typeAheadSuggestions}
          virtualCareProviders={uniqueVirtualCareProviders}
        />
      ) : (
        (autoCompleteCallCompleted || specialCharCheck) &&
        !isLoading &&
        !enter && (
          <SuggestionListContainer
            data-auto-testid="suggestion-list-container"
            data-testid="suggestion-list-containter"
          >
            <DropdownContainer
              data-testid="primary-dropdown-container"
              role="listbox"
            >
              <GuestDropdownBeforeMessage
                show={showGuestTypeaheadCoverageLink}
              />

              <SuggestionSectionItem
                className={`first-child ${activeSuggestion === 0}`}
                {...(isLLMGeneralSearchEnabled && { role: 'option' })}
              >
                {isLLMGeneralSearchEnabled ? (
                  <UserTextSearch
                    data-testid="user-text-search-common-suggestion"
                    keywordSearchTerm={keywordSearchTerm}
                    onClick={handleGeneralSearchClick}
                  />
                ) : (
                  <span
                    className="no-results"
                    data-testid="suggestion-list-no-results"
                    role="alert"
                  >
                    {setMessage()}
                  </span>
                )}
              </SuggestionSectionItem>
            </DropdownContainer>
          </SuggestionListContainer>
        )
      )}
    </React.Fragment>
  );
};
